import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Empty, Spin, Alert } from "antd";
import { useTranslation } from "react-i18next";

import { DidacticRoutesAPI, StudentAPI } from "../../../../api";
import { QuizApi } from "../../../../api/quiz";
import { BrandThemeProvider, SceneCard } from "../../../../ui";

import { RouteCard } from "./RouteCard";
import { ClassPanel } from "./ClassPanel";
import { SceneViewer } from "./SceneViewer";
import { QuizSelectModal } from "./QuizSelectModal";
import { RankingModal } from "./RankingModal";
import styles from "./ItinerariosView.module.scss";

const routesAPI = new DidacticRoutesAPI();
const quizApi = new QuizApi();
const studentAPI = new StudentAPI();

/**
 * ItinerariosView — pantalla Itinerarios redisenada (F4, AntD).
 * Cards de itinerario + escenas en lista + panel de clase + visor de escena.
 */
export function ItinerariosView({ teacher, accessToken }) {
  const { t } = useTranslation("evaluations");
  const teacherId = teacher?._id;
  const institutionId = teacher?.institution?._id;

  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routeError, setRouteError] = useState("");

  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedClassNumber, setSelectedClassNumber] = useState(null);

  const [scenes, setScenes] = useState([]);
  const [loadingScenes, setLoadingScenes] = useState(false);

  const [quizzesList, setQuizzesList] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);

  // Visor de escena
  const [sceneData, setSceneData] = useState(null);
  const [iframeShortLink, setIframeShortLink] = useState(null);
  const [iframeFallbackUrl, setIframeFallbackUrl] = useState(null);

  // Modal de test
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedSceneForQuiz, setSelectedSceneForQuiz] = useState(null);
  const [selectedQuizId, setSelectedQuizId] = useState(null);

  // Ranking
  const [activeQuizId, setActiveQuizId] = useState(null);
  const [rankingQuizName, setRankingQuizName] = useState("");
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [rankingData, setRankingData] = useState([]);

  // ---- Carga itinerarios + quizzes ----
  useEffect(() => {
    let ignore = false;
    if (!teacherId) return undefined;
    (async () => {
      try {
        setLoadingRoutes(true);
        setRouteError("");
        const res = await routesAPI.getRoutesByTeacher(accessToken, teacherId);
        if (!ignore) setRoutes(res?.routes || []);
      } catch (e) {
        if (!ignore) setRouteError(e?.message || t("rutasPanel.loadError"));
      } finally {
        if (!ignore) setLoadingRoutes(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [teacherId, accessToken, t]);

  useEffect(() => {
    let ignore = false;
    if (!teacherId) return undefined;
    (async () => {
      try {
        setLoadingQuizzes(true);
        const { quizzes } = await quizApi.getAllQuizzes(accessToken, teacherId);
        if (!ignore) setQuizzesList(quizzes || []);
      } catch (e) {
        if (!ignore) setQuizzesList([]);
      } finally {
        if (!ignore) setLoadingQuizzes(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [teacherId, accessToken]);

  // ---- Carga escenas de la ruta ----
  useEffect(() => {
    let ignore = false;
    if (!selectedRouteId) {
      setScenes([]);
      return undefined;
    }
    (async () => {
      try {
        setLoadingScenes(true);
        const res = await routesAPI.getScenesByRoute(
          accessToken,
          selectedRouteId,
          1,
          50,
        );
        if (!ignore) setScenes(res?.scenes || []);
      } catch (e) {
        if (!ignore) setScenes([]);
      } finally {
        if (!ignore) setLoadingScenes(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [selectedRouteId, accessToken]);

  const resetViewer = () => {
    setSceneData(null);
    setIframeShortLink(null);
    setIframeFallbackUrl(null);
  };

  const handleSelectRoute = useCallback((route) => {
    setSelectedRouteId(route._id);
    setSelectedClassNumber(route.classnumber);
    setActiveQuizId(null);
    setRankingQuizName("");
    resetViewer();
  }, []);

  const openScene = useCallback((scene) => {
    setSceneData(scene);
    if (scene.shortLink && scene.shortLink !== "No shortLink") {
      setIframeShortLink(scene.shortLink);
      setIframeFallbackUrl(null);
    } else {
      setIframeShortLink(null);
      setIframeFallbackUrl(scene.urlsala);
    }
  }, []);

  const handleEnterScene = useCallback(
    (scene) => {
      if (!quizzesList.length) {
        openScene(scene);
        setActiveQuizId(null);
        setRankingQuizName("");
      } else {
        setSelectedSceneForQuiz(scene);
        setSelectedQuizId(null);
        setShowQuizModal(true);
      }
    },
    [quizzesList.length, openScene],
  );

  const handleConfirmQuiz = useCallback(async () => {
    try {
      await quizApi.setActiveTeacherQuiz(
        accessToken,
        teacherId,
        selectedQuizId,
      );
      setActiveQuizId(selectedQuizId);
      const q = quizzesList.find((x) => x._id === selectedQuizId);
      setRankingQuizName(q?.name || "");
    } catch (e) {
      // noop
    }
    setShowQuizModal(false);
    openScene(selectedSceneForQuiz);
  }, [
    accessToken,
    teacherId,
    selectedQuizId,
    quizzesList,
    selectedSceneForQuiz,
    openScene,
  ]);

  const handleSkipQuiz = useCallback(async () => {
    try {
      await quizApi.setActiveTeacherQuiz(accessToken, teacherId, null);
    } catch (e) {
      // noop
    }
    setActiveQuizId(null);
    setRankingQuizName("");
    setShowQuizModal(false);
    if (selectedSceneForQuiz) openScene(selectedSceneForQuiz);
  }, [accessToken, teacherId, selectedSceneForQuiz, openScene]);

  const handleRankingClick = useCallback(
    async (quizId) => {
      setShowRankingModal(true);
      setLoadingRanking(true);
      try {
        const q = quizzesList.find((x) => x._id === quizId);
        setRankingQuizName(q?.name || "");
        const result = await quizApi.getRankingResults(
          accessToken,
          teacherId,
          quizId,
          selectedClassNumber,
        );
        const ranking = result?.ranking || [];
        const studentsRes = await studentAPI.getStudentsByClassnumber(
          accessToken,
          institutionId,
          selectedClassNumber,
        );
        const list = studentsRes?.students || [];
        setRankingData(
          ranking.map((item) => ({
            ...item,
            avatar: list.find((s) => s._id === item.studentId)?.avatar,
          })),
        );
      } catch (e) {
        setRankingData([]);
      } finally {
        setLoadingRanking(false);
      }
    },
    [accessToken, teacherId, institutionId, selectedClassNumber, quizzesList],
  );

  const hasRoute = !!selectedRouteId;

  const sceneList = useMemo(
    () =>
      scenes.map((s) => (
        <SceneCard
          key={s._id}
          scene={s}
          enterLabel={t("rutasPanel.enterButton")}
          onEnter={handleEnterScene}
        />
      )),
    [scenes, handleEnterScene, t],
  );

  return (
    <BrandThemeProvider>
      <div className={styles.wrap}>
        {/* Fila de itinerarios */}
        <Card
          className={styles.card}
          variant="outlined"
          styles={{ body: { padding: 14 } }}
        >
          {loadingRoutes ? (
            <div className={styles.center}>
              <Spin />
            </div>
          ) : routeError ? (
            <Alert type="error" showIcon message={routeError} />
          ) : routes.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("rutasPanel.noItineraries")}
            />
          ) : (
            <div className={styles.routesRow}>
              {routes.map((r) => (
                <RouteCard
                  key={r._id}
                  route={r}
                  selected={r._id === selectedRouteId}
                  onEnter={handleSelectRoute}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Contenido segun seleccion */}
        {!hasRoute ? (
          <Card
            className={styles.card}
            variant="outlined"
            styles={{ body: { padding: 32 } }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("scenesPanel.selectItinerary")}
            />
          </Card>
        ) : (
          <div className={styles.columns}>
            <Card
              className={styles.card}
              variant="outlined"
              styles={{ body: { padding: 16 } }}
            >
              {sceneData ? (
                <SceneViewer
                  scene={sceneData}
                  shortLink={iframeShortLink}
                  fallbackUrl={iframeFallbackUrl}
                  accessToken={accessToken}
                  routeId={selectedRouteId}
                  activeQuizName={rankingQuizName}
                  onClose={resetViewer}
                />
              ) : (
                <>
                  <div className={styles.colTitle}>
                    {t("itinerarios.scenesTitle")}
                  </div>
                  {loadingScenes ? (
                    <div className={styles.center}>
                      <Spin />
                    </div>
                  ) : scenes.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t("scenesPanel.noItineraries")}
                    />
                  ) : (
                    <div className={styles.sceneList}>{sceneList}</div>
                  )}
                </>
              )}
            </Card>

            <Card
              className={styles.card}
              variant="outlined"
              styles={{ body: { padding: 16 } }}
            >
              <ClassPanel
                institutionId={institutionId}
                classNumber={selectedClassNumber}
                route={selectedRouteId}
                accessToken={accessToken}
                activeQuizId={activeQuizId}
                onRankingClick={handleRankingClick}
              />
            </Card>
          </div>
        )}

        <QuizSelectModal
          open={showQuizModal}
          loading={loadingQuizzes}
          quizzes={quizzesList}
          selectedQuizId={selectedQuizId}
          onSelect={setSelectedQuizId}
          onSkip={handleSkipQuiz}
          onConfirm={handleConfirmQuiz}
        />

        <RankingModal
          open={showRankingModal}
          onClose={() => setShowRankingModal(false)}
          loading={loadingRanking}
          rankingQuizName={rankingQuizName}
          rankingData={rankingData}
          classNumber={selectedClassNumber}
        />
      </div>
    </BrandThemeProvider>
  );
}

export default ItinerariosView;
